/**
 * sl-xtes.3: Pattern matcher for AI key-related execution errors.
 *
 * Detects execution failures caused by missing or invalid AI API keys.
 * Used by ExecutionPanel and AutomationFailedNotification.
 */

/**
 * Known error patterns from backend ai-gateway.service.ts and external AI APIs.
 */
const AI_KEY_ERROR_PATTERNS: Array<{
  pattern: RegExp;
  providerExtractor?: (match: RegExpMatchArray) => string | undefined;
}> = [
  // Backend: ai-gateway.service.ts → "No API key configured for provider 'X'"
  {
    pattern: /No API key configured for provider '([^']+)'/i,
    providerExtractor: m => m[1],
  },
  // Backend: ai-provider.service.ts → "No API key available for X"
  {
    pattern: /No API key available for ([^.]+)/i,
    providerExtractor: m => m[1],
  },
  // Backend: HTTP 412 code
  { pattern: /NO_API_KEY/i },
  // External APIs: authentication failures
  { pattern: /Invalid API key/i },
  { pattern: /Incorrect API key/i },
  // Backend: automation tools → "No API key configured for AI provider(s): X, Y"
  {
    pattern: /No API key configured for AI provider\(s\): ([^.]+)/i,
    providerExtractor: m => m[1],
  },
];

export interface AiKeyErrorResult {
  isKeyError: boolean;
  provider?: string;
}

/**
 * Check if an error message indicates an AI key issue.
 * Returns the detected provider name if available.
 */
export function isAiKeyError(errorMessage: string): AiKeyErrorResult {
  for (const { pattern, providerExtractor } of AI_KEY_ERROR_PATTERNS) {
    const match = errorMessage.match(pattern);
    if (match) {
      return {
        isKeyError: true,
        provider: providerExtractor?.(match)?.trim(),
      };
    }
  }
  return { isKeyError: false };
}
